import { useCallback, useImperativeHandle, useLayoutEffect, useRef } from "react";
import { DOMCursor } from "./DOMCursor";
import { PreCompiledBlockDefinition } from "./PreCompiledBlockDefinition";
import { blockRootDataRootAttribute } from "./block-root";
import { BlockInstance, substituteMarkupWithUserPlaceholders } from "@softr-io/blocks-sdk";
import { usePageContext } from "./PageContext";
import { isCustomCodeBlock } from "./BlockDefinitionUtils";
import { executeScripts } from "./executeScripts";
import { nodesBetween } from "./dom-utils/nodesBetween";
import { useAppConstants } from "./AppConstantsContext";

type Props = Readonly<{
    definition: PreCompiledBlockDefinition;
    topLevel: boolean;
    hiddenDueToPreviousBlockLoading?: boolean;
}>;

export function PreCompiledBlock({ definition, topLevel, hiddenDueToPreviousBlockLoading }: Props) {
    const { setBlockInstanceRef } = usePageContext();
    const ref = useCallback(
        (instance: BlockInstance) => setBlockInstanceRef(definition.id, instance),
        [definition.id, setBlockInstanceRef],
    );
    useImperativeHandle(ref, () => ({}));
    // Even though both legacy custom code and other pre-compiled blocks are similar,
    // we have separate components for them because custom code blocks
    // must have their scripts disabled in the studio to prevent messing with
    // the studio layout.
    if (isCustomCodeBlock(definition)) {
        return (
            <CustomCodeBlockInFixedRootMode
                definition={definition}
                topLevel={topLevel}
                hiddenDueToPreviousBlockLoading={hiddenDueToPreviousBlockLoading}
            />
        );
    } else {
        return (
            <LegacyBlock
                definition={definition}
                topLevel={topLevel}
                hiddenDueToPreviousBlockLoading={hiddenDueToPreviousBlockLoading}
            />
        );
    }
}
function CustomCodeBlockInFixedRootMode({ definition, topLevel, hiddenDueToPreviousBlockLoading }: Props) {
    const { mode, blockIdAttributeName, slotIdAttributeName } = useAppConstants();
    const { topLevelDynamicBlockHrids } = usePageContext();
    const hidden = hiddenDueToPreviousBlockLoading;

    const scriptRef = useRef<HTMLScriptElement>(null);
    const isExecutedRef = useRef(false);
    const sentinelStartRef = useRef<HTMLDivElement>(null);
    const sentinelEndRef = useRef<HTMLDivElement>(null);
    const rootElementRef = useRef<HTMLElement | null>(null);
    const dataBlock = blockRootDataRootAttribute(definition);

    useLayoutEffect(() => {
        // WARNING: The behaviour of `classList.toggle("something", undefined)` is different across browsers and OS
        // versions. If you remove !! from the following line, the "custom-code" spec fails locally on macos and also in
        // the docker container. However, in CI, it passes :/
        rootElementRef.current?.classList.toggle("spr-hidden", !!hidden);
    }, [hidden]);

    useLayoutEffect(() => {
        // Add the markup and execute all the scripts within `compiled_markup` and the
        // script tag inserted in the JSX below.

        if (mode === "studio") return;
        if (isExecutedRef.current) return;
        isExecutedRef.current = true;

        const sentinelStart = sentinelStartRef.current;
        const sentinelEnd = sentinelEndRef.current;
        if (!sentinelStart || !sentinelEnd) {
            throw new Error("Sentinel nodes not found; Are the refs attached?");
        }
        const contentRoot = sentinelStart.parentElement;
        if (!contentRoot) {
            throw new Error("Orphan sentinel node found");
        }
        const cursor = new DOMCursor(contentRoot, sentinelStart);
        const nodes = injectMarkup(cursor, { compiled_markup: definition.compiled_markup });
        const root = rootNode(nodes);
        rootElementRef.current = root;
        rootElementRef.current?.setAttribute("data-spr-block-id", definition.id);
        if (root) {
            root.setAttribute("data-block", dataBlock);
        }
        const runScripts = async () => {
            if (topLevel) {
                await waitForElementsById(topLevelDynamicBlockHrids);
            }
            for (const node of nodesBetween(sentinelStart, sentinelEnd)) {
                if (node instanceof Element) {
                    try {
                        await executeScripts(node);
                    } catch (e) {
                        console.error("Custom code error", e);
                        // Depliberately ignore the error because we don't want a bad script to prevent
                        // the dispatching the custom event. That event is signals to the top level
                        // render function that it can dispatch "DOMContentLoaded"
                    }
                }
            }

            // Signal to the top level render function that it can dispatch the "DOMContentLoaded" event now
            window.dispatchEvent(
                new CustomEvent("softr/legacy-custom-code-scripts-executed", { detail: { id: definition.id } }),
            );
        };
        if (mode !== "studio") {
            runScripts();
        }
    }, [definition.compiled_markup, mode, dataBlock, topLevel, topLevelDynamicBlockHrids, definition.id]);
    if (mode === "studio") {
        const attrs: Record<string, string> = {
            "data-block": blockRootDataRootAttribute(definition),
        };
        if (topLevel && blockIdAttributeName) {
            attrs[blockIdAttributeName] = definition.id;
        }
        if (!topLevel && slotIdAttributeName) {
            attrs[slotIdAttributeName] = definition.id;
        }
        return (
            <div
                id={definition.hrid}
                dangerouslySetInnerHTML={{
                    // Custom code blocks should not show any content in the studio.
                    // Even without javascript, custom code can mess up the studio layout.
                    // For example, you could have custom code like
                    //   <div
                    //      style="position: fixed; z-index: 100000, top: 0, left: 0, width: 100%; height: 100%; background: white;">
                    //   </div>
                    // This would make the studio unusable.
                    __html: CUSTOM_CODE_WARNING_IN_STUDIO,
                }}
                {...attrs}
            />
        );
    }

    // NOTE: The `compiled_markup` already contains a block root element which the studio adds
    return (
        <>
            <div data-spr-sentinel-start-for={definition.id} data-spr-role="sentinel-start" ref={sentinelStartRef} />
            <style
                data-spr-hrid={definition.hrid}
                data-spr-role="custom-code-compiled_style"
                dangerouslySetInnerHTML={{ __html: definition.compiled_style }}
            />
            <script
                data-spr-hrid={definition.hrid}
                data-spr-role="custom-code-compiled_script"
                dangerouslySetInnerHTML={{ __html: definition.compiled_script }}
                ref={scriptRef}
            />
            <div data-spr-sentinel-end-for={definition.id} data-spr-role="sentinel-end" ref={sentinelEndRef} />
        </>
    );
}

function LegacyBlock({ definition, hiddenDueToPreviousBlockLoading }: Props) {
    const sentinelStartRef = useRef<HTMLDivElement>(null);
    const { mode, blockIdAttributeName } = useAppConstants();
    const rootElementRef = useRef<HTMLElement | null>(null);
    const hidden = hiddenDueToPreviousBlockLoading;

    useLayoutEffect(() => {
        const sentinel = sentinelStartRef.current;
        if (!sentinel) {
            throw new Error("Orphan sentinel node found");
        }
        const contentRoot = sentinel.parentElement;
        if (!contentRoot) {
            throw new Error("Orphan sentinel node found");
        }

        const abortController = new AbortController();
        const nodes: Node[] = [];
        const cursor = new DOMCursor(contentRoot, sentinel);
        const def = {
            compiled_markup: definition.compiled_markup,
            compiled_script: definition.compiled_script,
            compiled_style: definition.compiled_style,
            id: definition.id,
            name: definition.name,
            version: definition.version,
        } as const;
        nodes.push(...injectMarkup(cursor, def), ...injectStyle(cursor, def), ...injectScript(cursor, def));

        if (mode === "studio" && def.name === "map1") {
            // Studio has a special logic for google maps block
            // we need to replicate this in the page renderer
            nodes.push(injectGoogleMapsScript(cursor));
        }

        // Attach the block id attribute to the root node so that the studio can identify it
        const root = rootNode(nodes);
        root?.setAttribute("data-spr-block-id", definition.id);
        if (root && blockIdAttributeName) {
            root.setAttribute(blockIdAttributeName, definition.id);
        }
        rootElementRef.current = root;

        (async function runScripts() {
            for (const node of nodes) {
                if (abortController.signal.aborted) {
                    return;
                }
                if (!(node instanceof HTMLElement)) {
                    continue;
                }
                await executeScripts(node);
            }
        })();

        return () => {
            abortController.abort();
            for (const node of nodes) {
                node.parentNode?.removeChild(node);
            }
            rootElementRef.current = null;
        };
    }, [
        definition.name,
        definition.compiled_markup,
        definition.compiled_script,
        definition.compiled_style,
        definition.id,
        definition.version,
        blockIdAttributeName,
        mode,
    ]);

    useLayoutEffect(() => {
        rootElementRef.current?.classList.toggle("spr-hidden", hidden);
    }, [hidden]);

    return (
        <>
            <div data-spr-sentinel-start-for={definition.id} data-spr-role="sentinel-start" ref={sentinelStartRef} />
            <div data-spr-sentinel-end-for={definition.id} data-spr-role="sentinel-end" />
        </>
    );
}

function injectMarkup(
    cursor: DOMCursor,
    { compiled_markup }: Pick<PreCompiledBlockDefinition, "compiled_markup">,
): Node[] {
    const tempNode = document.createElement("div");
    const windowUser = window.logged_in_user;
    tempNode.innerHTML = substituteMarkupWithUserPlaceholders(compiled_markup, windowUser, false);

    const result: Node[] = [];
    for (const child of Array.from(tempNode.childNodes)) {
        cursor.appendChild(child);
        result.push(child);
    }
    return result;
}

function injectScript(
    cursor: DOMCursor,
    { compiled_script, id }: Pick<PreCompiledBlockDefinition, "compiled_script" | "id">,
): Node[] {
    // The reason why we're inserting the script in a weird way here
    // is because we want to prevent the script from being executing right now.
    //
    // We want the scripts to be patched for DOMContentLoaded events (in evalScripts.ts).
    //
    // The script will immediately execute if we do the following obvious thing:
    //   const script = document.createElement("script")
    //   script.innerHTML = compiled_script
    //   document.appendChild(script)

    const result: Node[] = [];

    const comment = document.createComment(`compiled_script for ${id}`);
    cursor.appendChild(comment);
    result.push(comment);

    const div = document.createElement("div");
    div.innerHTML = `<script>${compiled_script}</script>`;
    Array.from(div.children).forEach((node) => {
        cursor.appendChild(node);
        result.push(node);
    });

    return result;
}

function injectStyle(
    cursor: DOMCursor,
    { compiled_style }: Pick<PreCompiledBlockDefinition, "compiled_style">,
): Node[] {
    const styleTag = document.createElement("style");
    styleTag.innerHTML = compiled_style;
    cursor.appendChild(styleTag);
    return [styleTag];
}

function injectGoogleMapsScript(cursor: DOMCursor): HTMLElement {
    const mapsScript = document.createElement("script");
    mapsScript.src = "https://maps.googleapis.com/maps/api/js?key=";
    cursor.appendChild(mapsScript);
    return mapsScript;
}

/**
 * V1 and V2 blocks don't have a predictable root node.
 * This function tries to guess the root node based on hueristics.
 * If we find more cases, this is where we'll add them
 */
function rootNode(nodes: Node[]): HTMLElement | null {
    // Some v1 blocks can have a script tag as the first node
    // None of the below are valid nodes for a block:
    const invalidTags = ["SCRIPT", "STYLE", "COMMENT"];
    const validNodes = nodes.filter((node) => {
        if (node instanceof HTMLElement) {
            return !invalidTags.includes(node.tagName);
        }
        return true;
    });

    for (const node of validNodes) {
        // Most v2 blocks have a root div with a data-block-id attribute
        if (node instanceof HTMLElement && node.hasAttribute("data-block-id")) {
            return node;
        }
        // Some v2 header blocks don't have a data-block-id attribute
        if (node instanceof HTMLElement && node.tagName === "NAV") {
            return node;
        }
    }
    // If all fails, treat the first HTMLElement node as the root
    for (const node of validNodes) {
        if (node instanceof HTMLElement) {
            return node;
        }
    }
    return null;
}

const CUSTOM_CODE_WARNING_IN_STUDIO = `
    <div style="text-align: center; padding: 20px; font-weight: 500;">
        Custom code does not currently render on the canvas. Open the preview mode to view it.
        We're working on improving this experience.
    </div>
`;

async function waitForElementsById(ids: readonly string[]): Promise<void> {
    while (ids.some((it) => !document.getElementById(it))) {
        await new Promise((resolve) => setTimeout(resolve));
    }
}
